/**
 * Per-app scheduled-tasks page.
 *
 * Read-only on create — schedules are registered via the MCP tool
 * `kin_schedule_task` or by the app's own code via `env.KIN.scheduleTask`.
 * The portal surfaces them for visibility + lets the owner run-now or
 * cancel.
 *
 * The page shows preview + live separately via a tab toggle so iterating
 * AI sessions don't accidentally cancel a production schedule. Default
 * tab is live.
 */

import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../api/client.js';
import { AppTabs } from '../components/AppTabs.js';
import { ManagerOnlyPage } from '../components/ManagerOnly.js';
import { IconClock, IconPlay, IconX } from '../components/Icons.js';
import {
  Banner,
  Button,
  Card,
  EmptyState,
  Mono,
  PageHeader,
  PageSkeleton,
} from '../components/ui.js';
import { relTime } from '../lib/format.js';
import { useAppPageWith } from '../lib/useAppPage.js';
import { runAction, type ActionMessage } from '../lib/runAction.js';

interface ScheduleRow {
  name: string;
  env: 'preview' | 'live';
  kind: 'one-time' | 'recurring';
  cron: string | null;
  next_run_at: number;
  next_run_at_iso: string;
  status: 'active' | 'completed' | 'cancelled';
  last_run_at: number | null;
  last_run_status: 'ok' | 'error' | 'timeout' | null;
  last_run_error: string | null;
  last_run_duration_ms: number | null;
  description: string | null;
}

export function Schedules() {
  const { slug } = useParams();
  const [env, setEnv] = useState<'preview' | 'live'>('live');
  const { app, payload, loading, error, setPayload } = useAppPageWith(
    slug,
    (s) =>
      api.get<{ schedules: ScheduleRow[] }>(
        `/api/apps/${encodeURIComponent(s)}/schedules?env=${env}`,
      ),
    [env],
  );
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<ActionMessage | null>(null);

  if (loading) return <PageSkeleton rows={4} />;
  if (error) return <Banner tone="error">{error}</Banner>;
  if (!app || !payload) return null;
  const schedules = payload.schedules;

  if (!app.is_manager) {
    return (
      <ManagerOnlyPage
        app={app}
        title="Schedules"
        description="Background tasks this app runs on a schedule."
      />
    );
  }

  async function runNow(name: string) {
    if (!slug) return;
    await runAction({
      do: () =>
        api.post(
          `/api/apps/${encodeURIComponent(slug)}/schedules/${encodeURIComponent(name)}/run-now?env=${env}`,
        ),
      onSuccess: () => `"${name}" will fire within ~60s.`,
      errorPrefix: "Couldn't run",
      setBusy: (b) => setBusy(b ? name : null),
      setMessage,
    });
  }

  async function cancel(name: string) {
    if (!slug) return;
    if (!confirm(`Cancel "${name}"? Any in-flight firing completes; no further runs will occur.`)) {
      return;
    }
    await runAction({
      do: () =>
        api.delete(
          `/api/apps/${encodeURIComponent(slug)}/schedules/${encodeURIComponent(name)}?env=${env}`,
        ),
      onSuccess: () => `Cancelled "${name}".`,
      errorPrefix: "Couldn't cancel",
      setBusy: (b) => setBusy(b ? name : null),
      setMessage,
    });
    // Optimistic local status flip — server confirmed.
    setPayload((prev) => ({
      schedules: prev.schedules.map((s) =>
        s.name === name ? { ...s, status: 'cancelled' as const } : s,
      ),
    }));
  }

  return (
    <div className="animate-fade-in">
      <PageHeader
        back={{ to: `/apps/${app.slug}`, label: app.name }}
        title="Schedules"
        description={
          <>
            Wake your app on a time or cron — calls <Mono>handleScheduled</Mono>. Register them with{' '}
            <Mono>kin_schedule_task</Mono> or from app code via <Mono>env.KIN.scheduleTask</Mono>.
          </>
        }
      />

      <div className="grid grid-cols-12 gap-6">
        <AppTabs
          slug={app.slug}
          isManager={app.is_manager}
          hasSchedules={app.has_schedules ?? false}
        />
        <main className="col-span-12 lg:col-span-9 xl:col-span-10 min-w-0 space-y-5">
          <div
            role="tablist"
            aria-label="Environment"
            className="inline-flex items-center gap-1 p-1 rounded-lg bg-surface-2 border border-line"
          >
            {(['live', 'preview'] as const).map((e) => (
              <button
                key={e}
                role="tab"
                aria-selected={env === e}
                type="button"
                onClick={() => setEnv(e)}
                className={
                  'px-3 py-1 rounded-md text-xs font-medium transition-colors capitalize focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/50 ' +
                  (env === e ? 'bg-surface text-ink shadow-card' : 'text-muted hover:text-ink')
                }
              >
                {e === 'live' ? 'Production' : 'Preview'}
              </button>
            ))}
          </div>

          {message && (
            <Banner tone={message.tone} onDismiss={() => setMessage(null)}>
              {message.text}
            </Banner>
          )}

          {schedules.length === 0 ? (
            <EmptyState
              icon={<IconClock size={18} />}
              title={`No schedules on ${env}`}
              description={
                <>
                  Register one with <Mono>kin_schedule_task</Mono>, or from app code with{' '}
                  <Mono>env.KIN.scheduleTask</Mono>.
                </>
              }
            />
          ) : (
            <Card>
              <ul className="divide-y divide-line">
                {schedules.map((s) => (
                  <li key={s.name} className="px-5 py-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Mono>{s.name}</Mono>
                          <ScheduleStatusPill schedule={s} />
                        </div>
                        {s.description && (
                          <p className="text-muted text-sm mt-1.5">{s.description}</p>
                        )}
                        <div className="text-xs text-muted mt-2 space-x-3">
                          <span>
                            {s.kind === 'recurring' ? (
                              <>
                                Cron <Mono>{s.cron}</Mono>
                              </>
                            ) : (
                              'One-time'
                            )}
                          </span>
                          <span>
                            Next: {s.status === 'active' ? formatTime(s.next_run_at) : '—'}
                          </span>
                          {s.last_run_at && (
                            <span>
                              Last: {s.last_run_status} ({relTime(s.last_run_at)})
                              {s.last_run_duration_ms != null && ` · ${s.last_run_duration_ms}ms`}
                            </span>
                          )}
                        </div>
                        {s.last_run_error && (
                          <p className="text-xs text-danger mt-2 font-mono break-all">
                            {s.last_run_error}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-none">
                        {s.status === 'active' && (
                          <>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => void runNow(s.name)}
                              loading={busy === s.name}
                              disabled={busy !== null}
                            >
                              <IconPlay size={11} />
                              Run now
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => void cancel(s.name)}
                              disabled={busy !== null}
                              className="!text-danger hover:!bg-danger-soft"
                            >
                              <IconX size={13} />
                              Cancel
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </main>
      </div>
    </div>
  );
}

function ScheduleStatusPill({ schedule }: { schedule: ScheduleRow }) {
  const isError = schedule.last_run_status === 'error' || schedule.last_run_status === 'timeout';
  const color =
    schedule.status === 'cancelled' || schedule.status === 'completed'
      ? 'bg-surface-2 text-muted ring-line'
      : isError
        ? 'bg-danger-soft text-danger ring-danger/20'
        : 'bg-ok-soft text-ok ring-ok/20';
  const label = schedule.status === 'active' ? (isError ? 'erroring' : 'active') : schedule.status;
  return (
    <span
      className={
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ' +
        color
      }
    >
      {label}
    </span>
  );
}

function formatTime(epochSec: number): string {
  const d = new Date(epochSec * 1000);
  const now = Date.now();
  const diff = d.getTime() - now;
  if (Math.abs(diff) < 60_000) return 'in <1 min';
  if (diff > 0 && diff < 60 * 60 * 1000) return `in ${Math.round(diff / 60_000)} min`;
  if (diff > 0 && diff < 24 * 60 * 60 * 1000) return `in ${Math.round(diff / 3_600_000)} h`;
  return d.toLocaleString();
}
